import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, X, Send } from "lucide-react";
import { Btn } from "./Btn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Enseigne lumineuse", "Lettres boîtiers / relief", "Caisson lumineux", "Enseigne drapeau",
  "Néon LED", "Totem", "Signalétique", "Vitrophanie / marquage vitrine", "Marquage véhicule",
  "Habillage façade", "Impression grand format", "Autre",
];
const BUDGETS = ["< 2 000 €", "2 000 – 5 000 €", "5 000 – 10 000 €", "10 000 – 25 000 €", "> 25 000 €", "Je ne sais pas"];

const inputCls =
  "w-full rounded-lg bg-ink-700 border border-chrome-dark text-white placeholder-slate-500 px-4 py-3 text-sm focus:border-signature focus:ring-1 focus:ring-signature outline-none transition-all";
const labelCls = "block text-xs font-mono uppercase tracking-wider text-chrome-mid mb-2";

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", city: "",
    project_type: "", dimensions: "", description: "", budget: "", desired_date: "",
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    setUploading(true);
    try {
      for (const file of selected) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/uploads`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, { id: data.id, name: data.original_filename }]);
      }
      toast.success("Fichier(s) ajouté(s)");
    } catch (err) {
      toast.error("Échec de l'envoi du fichier");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.description) {
      toast.error("Merci de remplir les champs obligatoires");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, file_ids: files.map((f) => f.id) });
      setDone(true);
      toast.success("Votre demande a bien été envoyée !");
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-white/15 bg-ink-800 p-10 text-center" data-testid="quote-success">
        <CheckCircle2 className="mx-auto h-14 w-14 text-signature" />
        <h3 className="mt-5 text-2xl font-bold text-white">Demande envoyée</h3>
        <p className="mt-3 text-slate-300">Merci {form.name.split(" ")[0]}. Nous revenons vers vous très rapidement pour étudier votre projet.</p>
        <Btn href="/" variant="secondary" className="mt-6">Retour à l'accueil</Btn>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/15 bg-ink-800 p-6 sm:p-10 relative overflow-hidden" data-testid="quote-form">
      <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-signature/10 blur-3xl" />
      <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Nom *</label>
          <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Votre nom" data-testid="quote-name" required />
        </div>
        <div>
          <label className={labelCls}>Entreprise</label>
          <input className={inputCls} value={form.company} onChange={set("company")} placeholder="Nom de l'entreprise" data-testid="quote-company" />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="vous@exemple.com" data-testid="quote-email" required />
        </div>
        <div>
          <label className={labelCls}>Téléphone *</label>
          <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="06 00 00 00 00" data-testid="quote-phone" required />
        </div>
        <div>
          <label className={labelCls}>Ville du projet</label>
          <input className={inputCls} value={form.city} onChange={set("city")} placeholder="Genève, Gex, Lyon…" data-testid="quote-city" />
        </div>
        <div>
          <label className={labelCls}>Type de projet</label>
          <select className={inputCls} value={form.project_type} onChange={set("project_type")} data-testid="quote-type">
            <option value="">Sélectionner…</option>
            {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Dimensions approximatives</label>
          <input className={inputCls} value={form.dimensions} onChange={set("dimensions")} placeholder="ex. 3 m x 0,5 m" data-testid="quote-dimensions" />
        </div>
        <div>
          <label className={labelCls}>Budget (facultatif)</label>
          <select className={inputCls} value={form.budget} onChange={set("budget")} data-testid="quote-budget">
            <option value="">Sélectionner…</option>
            {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Date souhaitée</label>
          <input className={inputCls} value={form.desired_date} onChange={set("desired_date")} placeholder="ex. Printemps 2026" data-testid="quote-date" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Description du projet *</label>
          <textarea className={inputCls + " min-h-[120px] resize-y"} value={form.description} onChange={set("description")} placeholder="Décrivez votre enseigne, votre façade, vos contraintes…" data-testid="quote-description" required />
        </div>

        <div className="sm:col-span-2">
          <label className={labelCls}>Logo, photo de façade, fichiers</label>
          <label className="flex cursor-pointer items-center justify-center gap-3 rounded-lg border border-dashed border-chrome-dark bg-ink-700/60 px-4 py-6 text-sm text-slate-400 hover:border-signature hover:text-white transition-colors" data-testid="quote-upload-label">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            {uploading ? "Envoi en cours…" : "Cliquez pour ajouter des fichiers (logo, façade, PDF…)"}
            <input type="file" multiple className="hidden" onChange={onFiles} data-testid="quote-file-input" accept="image/*,application/pdf,.ai,.eps,.svg" />
          </label>
          {files.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid="quote-file-list">
              {files.map((f) => (
                <li key={f.id} className="flex items-center justify-between rounded-md bg-ink-700 px-3 py-2 text-sm text-slate-300">
                  <span className="truncate">{f.name}</span>
                  <button type="button" onClick={() => removeFile(f.id)} className="text-chrome-mid hover:text-signature" aria-label="Retirer">
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Btn type="submit" size="lg" className="mt-7 w-full" disabled={submitting} data-testid="quote-submit">
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi…</> : <>Envoyer ma demande de devis <Send className="h-4 w-4" /></>}
      </Btn>
      <p className="mt-3 text-center text-xs text-chrome-dark">Réponse rapide • Étude et devis sans engagement</p>
    </form>
  );
}
